import React, { useEffect, useState } from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  TextField, Button, IconButton
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { tableCellClasses } from "@mui/material/TableCell";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import { getUserInfo } from "../Authservice";
import axios from "axios";
import { resources } from "../Resourses/Resources";


const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#2C268A",
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
  cursor: "grab",
}));

const SortableRow = ({
  row,
  index,
  onEdit,
  onDelete,
  isEditing,
  editedRow,
  handleEditChange,
  handleSave,
  handleCancel,
  isAdmin,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: row.hierarchyOrder.toString(),
    disabled: !isAdmin
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    backgroundColor: isDragging ? "#f0f0f0" : undefined,
    cursor: isAdmin ? "grab" : "default",
  };

  return (
    <StyledTableRow 
      ref={setNodeRef} 
      style={style} 
      {...attributes} 
      {...listeners}
    >
      <StyledTableCell>{index + 1}</StyledTableCell>
      <StyledTableCell>
        {isEditing ? (
          <TextField
            size="small"
            value={editedRow.memberName || ""}
            onChange={(e) => handleEditChange("memberName", e.target.value)}
          />
        ) : (
          row.memberName
        )}
      </StyledTableCell>
      <StyledTableCell>
        {isEditing ? (
          <TextField
            size="small"
            value={editedRow.position || ""}
            onChange={(e) => handleEditChange("position", e.target.value)}
          />
        ) : (
          row.position
        )}
      </StyledTableCell>
      {isAdmin && (
        <StyledTableCell onClick={(e) => e.stopPropagation()}>
          {isEditing ? (
            <>
              <IconButton onClick={() => handleSave(row.hierarchyOrder)}>
                <SaveIcon />
              </IconButton>
              <IconButton onClick={handleCancel}>
                <CancelIcon />
              </IconButton>
            </>
          ) : (
            <>
              <IconButton onClick={() => onEdit(row)}>
                <EditIcon style={{color:"#1976d2"}}/>
              </IconButton>
              <IconButton onClick={() => onDelete(row.hierarchyOrder)}>
                <DeleteIcon style={{color:"red"}}/>
              </IconButton>
            </>
          )}
        </StyledTableCell>
      )}
    </StyledTableRow>
  );
};

const AdministrationMembers = () => {
  const userRole = getUserInfo()?.roles[0];
  const isAdmin = userRole === "ROLE_MANAGEMENT";
  const [rows, setRows] = useState([]);
  const [displayOrder, setDisplayOrder] = useState([]); 
  const [newMember, setNewMember] = useState({ memberName: "", position: "" });
  const [editId, setEditId] = useState(null);
  const [editedRow, setEditedRow] = useState({});
  const [orderChanged, setOrderChanged] = useState(false); 
  
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  const getData =  async () => {
    
  
    try {
      const response = await axios.get(`${resources.APPLICATION_URL}getAllAcademicBodyMembers`);
      setRows(response.data);
      setDisplayOrder([...response.data]); // Initialize display order with the same data
      setOrderChanged(false);
    } catch (e) {
      console.error("Error fetching data:", e);
    }
  };
  
  useEffect(() => {
    getData();
  }, []);

  // Modified to only change the visual order without changing hierarchyOrder
  const handleDragEnd = (event) => {
    if (!isAdmin) return;
    
    const { active, over } = event;
    
    if (!active || !over || active.id === over.id) return;
    
    const oldIndex = displayOrder.findIndex(item => item.hierarchyOrder.toString() === active.id);
    const newIndex = displayOrder.findIndex(item => item.hierarchyOrder.toString() === over.id);
    
    if (oldIndex !== -1 && newIndex !== -1) {
      // Just reorder the display array without changing hierarchyOrder values
      const newDisplayOrder = arrayMove([...displayOrder], oldIndex, newIndex);
      setDisplayOrder(newDisplayOrder);
      setOrderChanged(true);
    }
  };

  // Function to save the current display order to the backend
  const saveOrder = async () => {
    try {
      // Create a new array where hierarchyOrder is updated to match current visual order
      // const updatedRows = displayOrder.map((row, idx) => ({
      //   ...row,
      //   hierarchyOrder: idx + 1
      // }));
      
      await axios.post(`${resources.APPLICATION_URL}saveAcademicBodyMembers`, displayOrder);
      alert('Order saved successfully');
      getData(); 
    } catch (e) {
      console.error("Error saving order:", e);
      alert('Failed to save order');
    }
  };

  const handleAdd = async () => {
    if (!newMember.memberName || !newMember.position) return;
    const highestOrder = rows.length > 0 
      ? Math.max(...rows.map(r => r.hierarchyOrder)) 
      : 0;
    
    const newRow = {
      memberName: newMember.memberName,
      position: newMember.position,
      // hierarchyOrder: highestOrder + 1
    };
    
    const updatedRows = [...rows, newRow];
    
    try {
      await axios.post(`${resources.APPLICATION_URL}saveAcademicBodyMembers`, updatedRows);
      getData(); // Refresh data after add
      setNewMember({ memberName: "", position: "" });
    } catch (e) {
      console.error("Error adding member:", e);
    }
  };

  const handleDelete = async (hierarchyOrder) => {
    if (!window.confirm("Are you sure you want to delete this member?")) {
      return;
    }
    
    try {
      const response = await axios.delete(`${resources.APPLICATION_URL}deleteAcademicBodyMember?orderId=${hierarchyOrder}`);
      alert('Successfully deleted');
      getData();
    } catch (e) {
      console.error("Error deleting member:", e);
    }
  };

  const handleEdit = (row) => {
    setEditId(row.hierarchyOrder);
    setEditedRow({ 
      memberName: row.memberName, 
      position: row.position 
    });
  };

  const handleEditChange = (field, value) => {
    setEditedRow(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async (hierarchyOrder) => {
    const updatedRows = rows.map(row => 
      row.hierarchyOrder === hierarchyOrder 
        ? { ...row, memberName: editedRow.memberName, position: editedRow.position } 
        : row
    );
    
    try {
      await axios.post(`${resources.APPLICATION_URL}saveAcademicBodyMembers`, updatedRows);
      getData();
      setEditId(null);
      setEditedRow({});
    } catch (e) {
      console.error("Error updating member:", e);
    }
  };

  const handleCancel = () => {
    setEditId(null);
    setEditedRow({});
  };



  return (
    <TableContainer component={Paper} sx={{ background: "white", maxWidth: 900, margin: "auto", mt: 2 }}>
      {isAdmin && (
        <div style={{ padding: 16, display: "flex", gap: 8, flexWrap: "wrap" }}>
          <TextField
            label="Member Name"
            value={newMember.memberName}
            onChange={e => setNewMember({ ...newMember, memberName: e.target.value })}
          />
          <TextField
            label="Position"
            value={newMember.position}
            onChange={e => setNewMember({ ...newMember, position: e.target.value })}
          />
          <Button variant="contained" onClick={handleAdd}>Add</Button>
          
          {orderChanged && (
            <Button 
              variant="contained" 
              color="success" 
              onClick={saveOrder}
              sx={{ ml: 2 }}
            >
              Save Order
            </Button>
          )}
        </div>
      )}
      <DndContext 
        sensors={sensors} 
        collisionDetection={closestCenter} 
        onDragEnd={handleDragEnd}
      >
        <SortableContext 
          items={displayOrder.map(row => row.hierarchyOrder.toString())} 
          strategy={verticalListSortingStrategy}
        >
          <Table>
            <TableHead>
              <TableRow>
                <StyledTableCell>S No</StyledTableCell>
                <StyledTableCell>Member Name</StyledTableCell>
                <StyledTableCell>Position</StyledTableCell>
                {isAdmin && <StyledTableCell>Actions</StyledTableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {displayOrder.map((row, index) => (
                <SortableRow
                  key={row.hierarchyOrder}
                  row={row}
                  index={index}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  isEditing={editId === row.hierarchyOrder}
                  editedRow={editedRow}
                  handleEditChange={handleEditChange}
                  handleSave={handleSave}
                  handleCancel={handleCancel}
                  isAdmin={isAdmin}
                />
              ))}
            </TableBody>
          </Table>
        </SortableContext>
      </DndContext>
    </TableContainer>
  );
};
export default AdministrationMembers;